import { ReactNode, useCallback, useMemo, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';

import { useToast } from '@kanda-libs/ks-design-library';

import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripePaymentElementChangeEvent } from '@stripe/stripe-js';

import { useCardPaymentFormContext } from '../Context';

interface CardPaymentFormElementsFormValues {
  acceptedTerms: boolean;
}

interface CardPaymentFormElementsHook {
  form: UseFormReturn<CardPaymentFormElementsFormValues>;
  onSubmit: () => void;
  isLoading: boolean;
  isSubmitting: boolean;
  showPropsError: boolean;
  disabled: boolean;
  stripe: Stripe | null;
  amount: number;
  title: string;
  showActionRequired: boolean;
  buttonLabel: string;
  titleClassName: string;
  customHeading: ReactNode;
  onElementChange: (e: StripePaymentElementChangeEvent) => void;
}

export default function useCardPaymentFormElements(): CardPaymentFormElementsHook {
  const {
    isLoading: jobIsLoading,
    clientSecret,
    paymentOption,
    jobId,
    title,
    showActionRequired,
    buttonLabel,
    showMobileTitle,
    customHeading,
  } = useCardPaymentFormContext();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);

  const { showError } = useToast();

  const form = useForm<CardPaymentFormElementsFormValues>({
    mode: 'onBlur',
  });

  const accepted = form.watch('acceptedTerms');

  const stripe = useStripe();
  const elements = useElements();

  const onElementChange = useCallback((e: StripePaymentElementChangeEvent) => {
    const { empty } = e;
    setIsEmpty(empty);
  }, []);

  const isLoading = useMemo(() => {
    if (jobIsLoading) return true;
    if (!stripe || !elements) return true;
    return false;
  }, [jobIsLoading, stripe, elements]);

  const showPropsError = useMemo(() => {
    if (isLoading) return false;
    if (!clientSecret || !paymentOption) return true;
    return false;
  }, [isLoading, clientSecret, paymentOption]);

  const disabled = useMemo(() => {
    if (isLoading || isSubmitting) return true;
    return !accepted || isEmpty;
  }, [isLoading, isSubmitting, accepted, isEmpty]);

  const amount = useMemo(
    () => paymentOption?.amount?.amount || 0,
    [paymentOption],
  );

  const onSubmit = useCallback(async () => {
    if (!jobId) {
      showError(
        'There has been an error generating your return URL - please refresh and try again. If this error persists, please cotact Kanda',
      );
      return;
    }
    if (!stripe || !elements) {
      showError(
        'There has been an error contacting Stripe - please refresh and try again. If this error persists, please cotact Kanda',
      );
      return;
    }
    setIsSubmitting(true);

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/success/${jobId}`,
      },
    });

    setIsSubmitting(false);
    if (!stripeError) {
      showError(
        'An unexpected error has occurred - please refresh and try again. If this error persists, please cotact Kanda',
      );
      return;
    }

    const { type, message } = stripeError;

    if (type === 'card_error' || type === 'validation_error') {
      showError(
        message ||
          'An unexpected error has occurred - please refresh and try again. If this error persists, please cotact Kanda',
      );
    }

    showError(
      'An unexpected error has occurred - please refresh and try again. If this error persists, please cotact Kanda',
    );
  }, [elements, stripe, jobId, showError]);

  const titleClassName = useMemo(() => {
    if (!showMobileTitle) return 'hidden md:block mb-6';
    return 'mb-6';
  }, [showMobileTitle]);

  return {
    form,
    onSubmit,
    isLoading,
    isSubmitting,
    showPropsError,
    disabled,
    stripe,
    amount,
    title,
    showActionRequired,
    buttonLabel,
    titleClassName,
    onElementChange,
    customHeading,
  };
}
